import React, { Dispatch, SetStateAction, useState } from "react";

import { Employee } from "manager/interfaces/api/employee";
import { Button, DropdownButton } from "shared/components/Button";
import Spacer from "shared/components/Spacer";
import { BackLinkProps, Pagination } from "shared/interfaces";
import TablePage from "shared/templates/TablePage";

import EmployeesTable from "./EmployeesTable";
import { SearchParams } from "./interface";

interface ExportEmployeeButtonProps {
  exportEmployees: () => void;
  exportAllEmployees: () => void;
  disabled: boolean;
}

const ExportEmployeeButton = ({
  exportEmployees,
  exportAllEmployees,
  disabled,
}: ExportEmployeeButtonProps) => (
  <DropdownButton
    type="secondary"
    light
    disabled={disabled}
    items={[
      {
        key: "all",
        label: "All Employees",
        onClick: exportAllEmployees,
      },
      {
        key: "filtered",
        label: "Filtered Employees",
        onClick: exportEmployees,
      },
    ]}
  >
    Export Employees
  </DropdownButton>
);

interface EmployeesProps {
  canInviteEmployee: boolean;
  onInviteEmployee: () => void;
  isEmployeeRolesDropdownLoading: boolean;
  employeesCount: number;
  handleSearchParams: (key: string, value: string) => void;
  searchParams: SearchParams;
  disableSerchAndFilters: boolean;
  employeeRoles: { label: string; key: string | number }[];
  handleAllSearchParams: (params: SearchParams) => void;
  exportEmployees: (params?: SearchParams) => void;
  isExportingEmployees: boolean;
  hasInputValue: boolean;
  setPagination: Dispatch<SetStateAction<Pagination>>;
  pagination: Pagination;
  backLink: BackLinkProps;
  isEmployeesLoading: boolean;
  employees: Employee[];
}

const Employees = ({
  canInviteEmployee,
  onInviteEmployee,
  isEmployeeRolesDropdownLoading,
  employeesCount,
  handleSearchParams,
  searchParams,
  disableSerchAndFilters,
  employeeRoles,
  handleAllSearchParams,
  exportEmployees,
  isExportingEmployees,
  hasInputValue,
  setPagination,
  pagination,
  backLink,
  isEmployeesLoading,
  employees,
}: EmployeesProps) => {
  const [showVeroAccessNote, setShowVeroAccessNote] = useState(false);

  const headerActions = canInviteEmployee
    ? [
        {
          key: "INVITE_EMPLOYEE",
          node: (
            <Button type="primary" onClick={onInviteEmployee}>
              Invite Employee
            </Button>
          ),
        },
      ]
    : undefined;

  return (
    <TablePage
      backLink={backLink}
      title="Employees"
      actions={headerActions}
      rightAlignActions={Boolean(headerActions)}
      tables={[
        {
          filterBar: {
            search: {
              action: (value) => handleSearchParams("nameOrEmail", value),
              placeholder: "Search by name or email",
              width: 340,
              debounce: true,
              value: searchParams.nameOrEmail,
              disabled: disableSerchAndFilters,
            },
            dropdownFilters: [
              {
                key: "roleId",
                label: "Role",
                value: searchParams.roleId,
                onChange: (selectedFilter) =>
                  handleSearchParams(
                    "roleId",
                    selectedFilter === "" ? null : selectedFilter
                  ),
                items: employeeRoles,
                disabled: disableSerchAndFilters,
              },
            ],
            checkboxFilters: [
              {
                key: "activeOnly",
                label: "Show deactivated",
                onChange: (targetValue) =>
                  handleSearchParams("activeOnly", targetValue),
                value: Boolean(searchParams.activeOnly),
                disabled: disableSerchAndFilters,
              },
            ],
            loadingDropdownOptions: isEmployeeRolesDropdownLoading,
            setFilters: handleAllSearchParams,
            action: {
              key: "EXPORT EMPLOYEES",
              node: (
                <ExportEmployeeButton
                  exportEmployees={() =>
                    exportEmployees({
                      ...searchParams,
                      activeOnly: searchParams.activeOnly ? undefined : true,
                    })
                  }
                  exportAllEmployees={() => exportEmployees({} as SearchParams)}
                  disabled={employeesCount === 0 || isExportingEmployees}
                />
              ),
            },
          },
          node: (
            <>
              <EmployeesTable
                hasInputValue={hasInputValue}
                setPagination={setPagination}
                pagination={pagination}
                employees={employees}
                loading={isEmployeesLoading}
                employeesCount={employeesCount}
                showDeactivated={Boolean(searchParams.activeOnly)}
              />
              <Spacer size={Spacer.SIZES.md} />
              <Button
                type="link"
                onClick={() => setShowVeroAccessNote((v) => !v)}
              >
                {showVeroAccessNote ? "Hide" : "Show"} Vero staff access note
              </Button>
              {showVeroAccessNote && (
                <>
                  <Spacer size={Spacer.SIZES.sm} />
                  <p>
                    Vero staff can access your account when you request support.
                    This does not grant ongoing access outside approved support
                    workflows.
                  </p>
                </>
              )}
            </>
          ),
        },
      ]}
    />
  );
};

export default Employees;
